import React, {Dispatch, SetStateAction} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory, useLocation} from 'react-router-dom';
import classNames from 'classnames';
import {IonToggle} from '@ionic/react';
import {Form} from 'formik';

import {Course, Store} from 'src/types/store';
import {saveAttendance} from 'shared/portfolio/actions/courses';

import Modal from 'shared/components/modal';
import Icon from 'shared/components/icon';
import Button from 'src/components/button';
import TextButton from 'src/components/TextButton';
import Section from 'shared/portfolio/components/section';
import CourseFormWrapper, {CourseForm} from './course-form-wrapper';
import MobileEditingSchoolSection from './mobile-editing-school-section';
import MobileSchoolSection from './mobile-school-section';
import styles from './school-year-section.module.scss';

interface Props {
  editing: boolean;
  setEditing: Dispatch<SetStateAction<boolean>>;
  classYear: string;
  year: string;
  initialValues: Record<string, CourseForm[]>;
  coursesByGradesAndSchool: Record<string, Record<string, Course[]>>;
}

const MobileSchoolYearSection = ({
  editing,
  setEditing,
  classYear,
  year,
  initialValues,
  coursesByGradesAndSchool,
}: Props) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const {pathname} = useLocation();
  const attendance = useSelector((state: Store) => state.attendance);

  return (
    <Section key={`bulk-entry-course-${year}`}>
      <div className={classNames('portfolio-section-wrapper-header', styles.bulk)}>
        <div>{classYear} Year</div>
        <div className={styles.mobileSubHeader}>
          {attendance[year] === 0 && (
            <>
              <div className={styles.attendance}>
                <Icon iconType="check" className={styles.check} /> Perfect Attendance
              </div>
              <div className={styles.mobileSeparator}>•</div>
            </>
          )}
          <TextButton
            className={styles.mobileEdit}
            onClick={() => setEditing(true)}
            testid="mobile-bulk-entry-edit-school-year"
          >
            Edit
          </TextButton>
        </div>
      </div>

      {Object.keys(coursesByGradesAndSchool).map((institutionId) => (
        <MobileSchoolSection
          key={institutionId}
          institutionId={institutionId}
          courseGroups={coursesByGradesAndSchool[institutionId]}
          classYear={classYear}
          onAddClick={() => setEditing(true)}
        />
      ))}

      <Modal show={editing} onClose={() => setEditing(false)}>
        <Modal.TextHeader onClose={() => setEditing(false)}>{classYear} Year</Modal.TextHeader>
        <CourseFormWrapper
          initialValues={initialValues}
          onSuccess={() => {
            setEditing(false);
          }}
        >
          {(formik) => (
            <Form autoComplete="off">
              <Modal.Body>
                <IonToggle
                  color="success"
                  checked={attendance[year] === 0}
                  className={styles.mobileToggle}
                  onClick={(e) => {
                    // @ts-expect-error: Ionic type doesn't define checked, but it exists
                    if (e.target.checked) {
                      dispatch(saveAttendance(year, 0));
                    } else {
                      dispatch(saveAttendance(year, 1));
                    }
                  }}
                >
                  <div className="shared-font body bold left">Perfect Attendance</div>
                  <div className={classNames('shared-font caption left', styles.mobileCaption)}>
                    No unexcused absences or tardies where you took most of your courses this school
                    year.
                  </div>
                </IonToggle>

                {Object.keys(coursesByGradesAndSchool).map((institutionId) => (
                  <MobileEditingSchoolSection
                    key={institutionId}
                    institutionId={institutionId}
                    courseGroups={coursesByGradesAndSchool[institutionId]}
                    numberInstitutionsWithCourses={Object.keys(coursesByGradesAndSchool).length}
                    year={year}
                  />
                ))}

                <div className={styles.mobileDivider}>
                  <Button
                    onClick={() =>
                      history.replace({
                        pathname: `${pathname}/school/new`,
                        state: {year, skipScrolling: true},
                      })
                    }
                    outline
                    size="small"
                    width="100%"
                  >
                    + Add School
                  </Button>
                </div>
              </Modal.Body>
              <Modal.Footer
                primaryButton={{
                  type: 'submit',
                  width: 120,
                  disabled: formik.isSubmitting || !formik.isValid,
                  text: formik.isSubmitting ? 'Saving...' : 'Save',
                }}
              />
            </Form>
          )}
        </CourseFormWrapper>
      </Modal>
    </Section>
  );
};

export default MobileSchoolYearSection;
